import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator as CalculatorIcon, Info } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import Calculator from '../components/Calculator';
import Input from '../components/Input';
import SEOHead from '../components/SEOHead';

// --- Australian novated lease model (2025-26 rates) -------------------------
// Verified figures (see FAQ for sources):
//   Resident income tax: 0% <=$18,200; 16% to $45k; 30% to $135k; 37% to $190k;
//   45% above; + 2% Medicare levy. FBT rate 47%, Type-1 gross-up 2.0802,
//   statutory taxable value 20% of base value. EV FBT exemption for battery
//   EV/FCEV under the fuel-efficient LCT threshold ($91,387 for 2025-26).
//   ATO minimum residuals by term: 1yr 65.63%, 2yr 56.25%, 3yr 46.88%,
//   4yr 37.50%, 5yr 28.13%.
// Model choices (stated in the UI): finance and running costs are packaged
// GST-exclusive (employer claims the GST input tax credit); FBT base value is
// the GST-inclusive price; ECM contributes the taxable value in post-tax
// dollars to reduce FBT to nil. This is an estimate, not tax advice.

const RESIDUAL_PCT: Record<number, number> = { 12: 0.6563, 24: 0.5625, 36: 0.4688, 48: 0.375, 60: 0.2813 };
const EV_LCT_THRESHOLD = 91387;
const FBT_RATE = 0.47;
const FBT_GROSSUP = 2.0802;
const STATUTORY_RATE = 0.20;

const incomeTaxPlusMedicare = (income: number): number => {
  if (income <= 0) return 0;
  let y = income;
  let t = 0;
  if (y > 190000) { t += (y - 190000) * 0.45; y = 190000; }
  if (y > 135000) { t += (y - 135000) * 0.37; y = 135000; }
  if (y > 45000) { t += (y - 45000) * 0.30; y = 45000; }
  if (y > 18200) { t += (y - 18200) * 0.16; }
  return t + income * 0.02; // + 2% Medicare levy
};

const monthlyWithResidual = (principal: number, annualRate: number, months: number, residual: number): number => {
  const i = annualRate / 100 / 12;
  if (i === 0) return (principal - residual) / months;
  const discount = Math.pow(1 + i, -months);
  const annuity = (1 - discount) / i;
  return (principal - residual * discount) / annuity;
};

const NovatedLeaseCalculator: React.FC = () => {
  const [vehiclePrice, setVehiclePrice] = useState('60000');
  const [salary, setSalary] = useState('100000');
  const [termYears, setTermYears] = useState('5');
  const [runningCosts, setRunningCosts] = useState('8000');
  const [interestRate, setInterestRate] = useState('7.00');
  const [isEV, setIsEV] = useState(false);
  const [useECM, setUseECM] = useState(true);

  const [result, setResult] = useState<null | {
    netCost: number; taxSaving: number; fbt: number; residual: number;
    packageCost: number; normalCost: number; annualSaving: number; evExempt: boolean;
  }>(null);

  useEffect(() => {
    const price = parseFloat(vehiclePrice);
    const sal = parseFloat(salary);
    const years = parseInt(termYears, 10);
    const running = parseFloat(runningCosts);
    const rate = parseFloat(interestRate);
    const months = years * 12;

    if (isNaN(price) || price <= 0 || isNaN(sal) || sal <= 0 || isNaN(running) || running < 0 || isNaN(rate) || rate < 0 || !RESIDUAL_PCT[months]) {
      setResult(null);
      return;
    }

    const resPct = RESIDUAL_PCT[months];
    const exGSTPrice = price / 1.1;              // employer claims GST credit
    const residual = exGSTPrice * resPct;
    const annualLease = monthlyWithResidual(exGSTPrice, rate, months, residual) * 12;
    const runningExGST = running / 1.1;
    const packageCost = annualLease + runningExGST;

    const baseValue = price;                      // FBT base value is GST-inclusive
    const taxableValue = STATUTORY_RATE * baseValue;

    // EV FBT exemption applies only under the LCT threshold.
    const evExempt = isEV && price <= EV_LCT_THRESHOLD;

    let fbt = 0;
    let preTaxDeduction = packageCost;
    if (evExempt) {
      fbt = 0;
      preTaxDeduction = packageCost;
    } else if (useECM) {
      fbt = 0;                                    // ECM offsets the taxable value
      preTaxDeduction = packageCost - taxableValue;
    } else {
      fbt = taxableValue * FBT_GROSSUP * FBT_RATE;
      preTaxDeduction = packageCost;
    }

    const taxSaving = incomeTaxPlusMedicare(sal) - incomeTaxPlusMedicare(sal - preTaxDeduction);
    const netCost = packageCost - taxSaving + fbt;

    // Baseline: financing the same car with a normal post-tax car loan, same
    // residual on the GST-inclusive price, running costs paid GST-inclusive.
    const normalResidual = price * resPct;
    const normalCost = monthlyWithResidual(price, rate, months, normalResidual) * 12 + running;

    setResult({
      netCost, taxSaving, fbt, residual, packageCost, normalCost,
      annualSaving: normalCost - netCost, evExempt,
    });
  }, [vehiclePrice, salary, termYears, runningCosts, interestRate, isEV, useECM]);

  const fmt = (v: number): string =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(v);

  return (
    <>
      <SEOHead
        title="Novated Lease Calculator Australia | Salary Packaging Car Calculator"
        description="Estimate the tax savings on a novated lease in Australia with 2025-26 rates. Compares statutory FBT, the Employee Contribution Method and the EV FBT exemption against a normal car loan."
        keywords="novated lease calculator australia, salary sacrifice car calculator, novated lease tax savings, salary packaging car, fringe benefits tax car, ev novated lease calculator, employee contribution method"
      />
      <Helmet>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FinancialProduct",
              "name": "Novated Lease Calculator",
              "description": "Estimate novated lease tax savings in Australia using 2025-26 FBT and income tax rates, including the EV FBT exemption.",
              "category": "LoanOrCredit",
              "feesAndCommissionsSpecification": "Free online calculator",
              "areaServed": { "@type": "Country", "name": "Australia" },
              "interactivityType": "active"
            }
          `}
        </script>
      </Helmet>
      <Calculator
        title="Novated Lease Calculator (Australia)"
        description="Estimate the tax savings from salary packaging a car on a novated lease, using 2025-26 FBT and income tax rates — with the Employee Contribution Method and the EV FBT exemption built in."
        icon={CalculatorIcon}
        result={result ? `Estimated saving: ${fmt(result.annualSaving)}/yr vs a normal loan` : undefined}
      >
        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-md mb-6">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
              <p className="text-sm text-blue-700">
                A novated lease lets you pay for a car and its running costs from your pre-tax salary, reducing your taxable income.
                Fringe Benefits Tax (FBT) applies — the Employee Contribution Method (ECM) or the EV exemption are used to reduce it to nil.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Input label="Vehicle Price (drive-away, inc GST)" value={vehiclePrice} onChange={setVehiclePrice} unit="$" placeholder="e.g. 60000" />
            <Input label="Annual Salary (before tax)" value={salary} onChange={setSalary} unit="$" placeholder="e.g. 100000" />
            <div className="form-control w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">Lease Term</label>
              <select
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                value={termYears}
                onChange={(e) => setTermYears(e.target.value)}
              >
                {[1, 2, 3, 4, 5].map((y) => (
                  <option key={y} value={y}>{y} year{y > 1 ? 's' : ''}</option>
                ))}
              </select>
            </div>
            <Input label="Annual Running Costs (fuel, rego, insurance, servicing)" value={runningCosts} onChange={setRunningCosts} unit="$" placeholder="e.g. 8000" />
            <Input label="Finance Rate (p.a.)" value={interestRate} onChange={setInterestRate} unit="%" placeholder="e.g. 7.00" />
            <div className="flex flex-col justify-center gap-2">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={isEV} onChange={(e) => setIsEV(e.target.checked)} className="h-4 w-4" />
                Eligible electric vehicle (FBT-exempt)
              </label>
              <label className={`flex items-center gap-2 text-sm ${isEV ? 'text-gray-400' : 'text-gray-700'}`}>
                <input type="checkbox" checked={useECM} disabled={isEV} onChange={(e) => setUseECM(e.target.checked)} className="h-4 w-4" />
                Use Employee Contribution Method (reduce FBT to nil)
              </label>
            </div>
          </div>

          {result && (
            <div className="mt-8 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <div className="bg-blue-600 text-white px-6 py-4">
                <h2 className="text-xl font-semibold">Estimated Annual Outcome</h2>
              </div>
              <div className="p-6">
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-green-50 p-4 rounded-lg text-center">
                    <div className="text-sm text-green-700">Est. Saving vs Normal Loan</div>
                    <div className="text-2xl font-bold text-green-700">{fmt(result.annualSaving)}<span className="text-sm font-normal">/yr</span></div>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <div className="text-sm text-blue-700">Net Cost to You</div>
                    <div className="text-2xl font-bold text-blue-800">{fmt(result.netCost)}<span className="text-sm font-normal">/yr</span></div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <div className="text-sm text-gray-600">Annual Tax Saving</div>
                    <div className="text-2xl font-bold text-gray-800">{fmt(result.taxSaving)}</div>
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div className="flex justify-between border-b py-1"><span className="text-gray-600">Total package cost</span><span className="font-medium">{fmt(result.packageCost)}/yr</span></div>
                  <div className="flex justify-between border-b py-1"><span className="text-gray-600">FBT payable</span><span className="font-medium">{result.evExempt ? '$0 (EV exempt)' : fmt(result.fbt)}</span></div>
                  <div className="flex justify-between border-b py-1"><span className="text-gray-600">Residual at end</span><span className="font-medium">{fmt(result.residual)} + GST</span></div>
                </div>
              </div>
            </div>
          )}

          {/* Assumptions + education */}
          <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold text-gray-800">How this estimate is calculated</h2>
            <p className="text-gray-600">
              We use 2025-26 rates: resident income tax (16% / 30% / 37% / 45% brackets) plus the 2% Medicare levy, the 47% FBT rate,
              the Type-1 gross-up of 2.0802 and the 20% statutory taxable value. Finance and running costs are packaged GST-exclusive
              (your employer claims the GST input tax credit), the residual uses the ATO minimum for your term, and the EV exemption
              applies to eligible electric vehicles priced under the fuel-efficient luxury car tax threshold of {fmt(EV_LCT_THRESHOLD)}.
              Compare a straight loan with our{' '}
              <Link className="text-blue-600 hover:underline" to="/car-loan-calculator/">car loan repayment calculator</Link> or weigh up
              a{' '}
              <Link className="text-blue-600 hover:underline" to="/lease-vs-buy-calculator/">lease vs buy</Link> decision.
            </p>

            <h2 className="text-xl font-semibold text-gray-800">Frequently asked questions</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-700">What is the Employee Contribution Method (ECM)?</h3>
                <p className="text-gray-600">
                  ECM means you pay the car's taxable value (20% of its base value) from your post-tax salary. This reduces the FBT to
                  nil, so the rest of the package can come from pre-tax income. Without ECM (and without the EV exemption), FBT can make
                  a novated lease more expensive than a normal loan.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Why do electric vehicles save more?</h3>
                <p className="text-gray-600">
                  Eligible battery-electric and hydrogen fuel-cell vehicles under the fuel-efficient LCT threshold ({fmt(EV_LCT_THRESHOLD)}
                  {' '}for 2025-26) are exempt from FBT, so the entire package can be salary-sacrificed pre-tax. Plug-in hybrids no longer
                  qualify for new arrangements entered from 1 April 2025.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">What happens to the residual at the end?</h3>
                <p className="text-gray-600">
                  At lease end you pay the residual (plus GST) to keep the car, or you can re-lease or trade it in. The ATO sets minimum
                  residuals by term. See how a lump sum works on our{' '}
                  <Link className="text-blue-600 hover:underline" to="/balloon-payment-calculator/">balloon payment calculator</Link>.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Is a novated lease better than a car loan?</h3>
                <p className="text-gray-600">
                  It depends on your salary, the car and whether it's an EV. Read our guide comparing a{' '}
                  <Link className="text-blue-600 hover:underline" to="/blog/novated-lease-vs-traditional-car-loan/">
                    novated lease vs a traditional car loan
                  </Link>.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Does this include GST savings on the purchase?</h3>
                <p className="text-gray-600">
                  Yes — the model finances and packages costs GST-exclusive because the employer claims the GST input tax credit, which
                  is a genuine novated-lease benefit. Your provider's quote may vary based on fees and packaged items.
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-500 pt-2">
              Estimates only, using 2025-26 rates, and not tax or financial advice. Actual outcomes depend on your employer's package,
              lease fees, running-cost estimates, your full tax position (including offsets and other income) and the FBT method used.
              Confirm figures with a licensed adviser and your novated lease provider.
            </p>
          </div>
        </div>
      </Calculator>
    </>
  );
};

export default NovatedLeaseCalculator;
